import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StockItemDetail, StockOwner } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Textarea } from "../../app/components/primitives/textarea";
import { OwnerPicker } from "./OwnerPicker";
import {
  FieldShell,
  type FieldErrorMap,
  toFieldErrorMap,
} from "./StockFormField";

interface ChangeOwnerDialogProps {
  item: StockItemDetail;
  onClose: () => void;
  onSaved: (detail: StockItemDetail) => void;
}

/** Change who owns the stock. Billing follows the owner, so the change needs a
 * reason and is recorded in the item's owner history. */
export function ChangeOwnerDialog({
  item,
  onClose,
  onSaved,
}: ChangeOwnerDialogProps) {
  const dataAdapter = useDataAdapter();
  const [owner, setOwner] = useState<StockOwner | null>(item.owner ?? null);
  const [reason, setReason] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSave() {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      onSaved(
        await dataAdapter.changeStockItemOwner(item.id, {
          owner_customer_code: owner?.customer_code ?? "",
          reason,
        }),
      );
    } catch (error) {
      console.error("stock.item.change_owner_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? "Please fix the highlighted fields and try again."
          : "Couldn't change the owner. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change owner</DialogTitle>
          <DialogDescription>
            {item.stock_code} · Now owned by{" "}
            {item.owner ? item.owner.name : "a customer no longer active"}.
            Billing follows the new owner's billing account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}
          <OwnerPicker
            id="change-owner-customer"
            label="New owner"
            value={owner}
            onChange={setOwner}
            error={fieldErrors.owner_customer_code}
          />
          <FieldShell
            id="change-owner-reason"
            label="Reason"
            error={fieldErrors.reason}
            hint="Recorded with your name in the owner history."
          >
            {(aria) => (
              <Textarea
                id="change-owner-reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                {...aria}
              />
            )}
          </FieldShell>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={
              isSaving ||
              owner === null ||
              owner.customer_id === item.owner_customer_id
            }
          >
            {isSaving ? "Saving…" : "Change owner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
